import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, debounce, filter, firstValueFrom, fromEvent, interval, map, merge, tap, timer } from 'rxjs';
import { AuthService } from 'src/app/auth/services/auth.service';
import { environment } from 'src/environments/environment';
import { StorageEstimate } from './offline-storage';
import { pdfViewerNeedsLegacyBuild } from 'src/app/shared/models/pdf-viewer-support';
import { OfflineStore } from './offline-store.service';
import { PdfViewerWarmup } from './pdf-viewer-warmup.service';
import { DocumentRemovedEvent, OfflineDocumentData, OfflineQuotaError, SnapshotKind, SnapshotRecord } from './offline.models';
import { SyncEngine } from './sync-engine.service';

export const SNAPSHOT_REFRESH_MS = 15 * 60 * 1000;
/** synced entries of one project are bundled into one snapshot request (review W6) */
export const SYNC_REFRESH_DEBOUNCE_MS = 2_000;

export type RefreshResult = 'updated' | 'unchanged' | 'skipped' | 'failed';

/** the error body of the Bavox API (BaseController.sendErrorResponse, AuthorizationMiddleware.guard): `{ success: false, error }` */
function isApiError(body: unknown): boolean {
  return typeof body === 'object' && body !== null && (body as { success?: unknown }).success === false && typeof (body as { error?: unknown }).error === 'string';
}

export function snapshotUrl(projectId: string): string {
  return `${environment.apiUrl}/projects/${encodeURIComponent(projectId)}/offline-snapshot`;
}

export interface OfflineProjectStatus {
  manual: boolean;
  auto: boolean;
  fetchedAt: number | null;
  size: number;
  /** saved plans of the project (step 4 Rev1 W8): switching the project off removes them */
  documentCount: number;
  documentSize: number;
}

export interface OfflineItemSummary {
  kind: SnapshotKind;
  projectId: string;
  refId: string;
  name: string;
  size: number;
  fetchedAt: number;
  manual: boolean;
  /** kept only because the project still has open outbox entries (re-review H13) */
  retained: boolean;
}

export interface OfflineSummary {
  projects: OfflineItemSummary[];
  documents: OfflineItemSummary[];
  totalSize: number;
  estimate: StorageEstimate | null;
}

interface SnapshotResponse {
  data: { etag: string; project: { id: string; projectName: string } } & Record<string, unknown>;
}

/**
 * Which projects (and later documents) live on the device and keeping their snapshots fresh (spec §3): the last
 * opened project automatically (data only), others by hand. Refresh on opening, online, every 15 min while visible and
 * after a sync of the project (bundled). Every write goes to the user who started the work (review W4).
 */
@Injectable({ providedIn: 'root' })
export class OfflineAvailabilityService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(OfflineStore);
  private readonly engine = inject(SyncEngine);
  private readonly warmup = inject(PdfViewerWarmup);
  private readonly auth = inject(AuthService);
  private readonly availableSubject = new BehaviorSubject<ReadonlySet<string>>(new Set());
  readonly available$ = this.availableSubject.asObservable();
  private readonly chosenSubject = new BehaviorSubject<ReadonlySet<string>>(new Set());
  /**
   * projects on the device that the user switched on himself («Offline verfügbar», settings.projectIds), without the
   * automatic last opened or retained ones: only these show the cloud (Tester-Feedback R1, Punkt 13/14). The keeping
   * rules (available$, trackedIds) are unchanged.
   */
  readonly chosen$ = this.chosenSubject.asObservable();
  private readonly quotaSubject = new Subject<void>();
  /** a snapshot did not fit on the device */
  readonly quota$ = this.quotaSubject.asObservable();
  private readonly documentsRemovedSubject = new Subject<DocumentRemovedEvent[]>();
  /** saved files that left the device on a refresh: deleted, replaced or no access (step 4, plan E5); AppComponent tells the user */
  readonly documentsRemoved$ = this.documentsRemovedSubject.asObservable();
  private triggers: Subscription | null = null;
  /** one request per project at a time (review W6); requireTracked decides whether a call may join it */
  private readonly inFlight = new Map<string, { promise: Promise<RefreshResult>; requireTracked: boolean }>();
  /** calls that must not join the running request wait for one follow-up request (Schritt-1 FE-a M1) */
  private readonly followUps = new Map<string, Promise<RefreshResult>>();
  private readonly dirtyProjects = new Set<string>();
  private readonly frozenUsers = new Set<string>();
  /** IndexedDB write sections running per user (never the HTTP wait): freezeUser waits for them (Rev1 review I1) */
  private readonly writing = new Map<string, Set<Promise<unknown>>>();

  /** test seams: clock in ms and the bundling window after synced$ */
  now: () => number = () => Date.now();
  refreshDebounceMs = SYNC_REFRESH_DEBOUNCE_MS;

  start(): void {
    if (this.triggers) return;
    void this.reloadAvailable();
    void this.warmPdfViewer();
    const synced$ = this.engine.synced$.pipe(
      tap((event) => this.dirtyProjects.add(event.projectId)),
      debounce(() => timer(this.refreshDebounceMs)),
      map(() => {
        const projectIds = [...this.dirtyProjects];
        this.dirtyProjects.clear();
        return projectIds;
      }),
    );
    this.triggers = merge(
      // the network is back: snapshots and, after an app update offline, the pdf viewer files (NA-20)
      fromEvent(window, 'online').pipe(tap(() => void this.warmPdfViewer()), map(() => null)),
      interval(SNAPSHOT_REFRESH_MS).pipe(filter(() => document.visibilityState === 'visible'), map(() => null)),
      synced$,
    ).subscribe((projectIds) => {
      if (projectIds === null) void this.refreshAll();
      else void this.afterSync(projectIds);
    });
  }

  stop(): void {
    this.triggers?.unsubscribe();
    this.triggers = null;
  }

  /**
   * Project overview opened (internal members only). The previous automatic project is only dropped once the new
   * snapshot is on the device and the previous project has no open outbox entries (review W3): opening a project
   * without network keeps what is there. A busy previous project goes to retainedProjectIds: still tracked (checkmark,
   * refresh) and dropped once its outbox is empty (re-review H13).
   */
  async markOpened(projectId: string): Promise<void> {
    const userId = this.store.currentUserId();
    if (!userId) return;
    const result = await this.fetch(projectId, userId);
    if (result !== 'updated' && result !== 'unchanged') return;
    await this.guarded(userId, async () => {
      // outbox first (async), then one atomic settings update on the current state (re-review H15)
      const outbox = await this.store.listOutbox(userId);
      if (!this.writesFor(userId)) return;
      let dropped: string | null = null;
      await this.store.updateSettings(userId, (settings) => {
        dropped = null;
        const previous = settings.lastOpenedProjectId;
        if (previous === projectId) return undefined;
        const retainedProjectIds = settings.retainedProjectIds.filter((id) => id !== projectId);
        const candidate = previous && !settings.projectIds.includes(previous) ? previous : null;
        const previousBusy = candidate !== null && outbox.some((entry) => entry.entityRef.projectId === candidate);
        if (candidate && previousBusy && !retainedProjectIds.includes(candidate)) retainedProjectIds.push(candidate);
        if (candidate && !previousBusy) dropped = candidate;
        return { ...settings, lastOpenedProjectId: projectId, retainedProjectIds };
      });
      if (dropped) await this.store.deleteSnapshot(dropped, 'project', dropped, userId);
      await this.pruneRetained(userId);
      await this.reloadAvailable();
    });
  }

  /** switch «Offline verfügbar» in the sheet of the project overview */
  async setAvailable(projectId: string, on: boolean): Promise<void> {
    const userId = this.store.currentUserId();
    if (!userId) return;
    if (on) {
      const { persistRequested } = await this.store.getSettings(userId);
      if (!persistRequested) await this.requestPersist();
      await this.store.updateSettings(userId, (settings) => ({
        ...settings,
        projectIds: settings.projectIds.includes(projectId) ? settings.projectIds : [...settings.projectIds, projectId],
        persistRequested: true,
      }));
      await this.fetch(projectId, userId);
    } else {
      // queued uploads keep the project on the device until they are on the server (Schritt-1 FE-b minor 3)
      const busy = await this.hasOutbox(userId, projectId);
      let drop = false;
      await this.store.updateSettings(userId, (settings) => {
        const kept = settings.lastOpenedProjectId === projectId || settings.retainedProjectIds.includes(projectId);
        drop = !kept && !busy;
        const retainedProjectIds = !kept && busy ? [...settings.retainedProjectIds, projectId] : settings.retainedProjectIds;
        return { ...settings, projectIds: settings.projectIds.filter((id) => id !== projectId), retainedProjectIds };
      });
      if (drop) await this.store.deleteSnapshot(projectId, 'project', projectId, userId);
      // saved plans belong to a project «Offline verfügbar» (plan E1): switched off, they leave too
      await this.dropDocuments(userId, projectId);
    }
    await this.reloadAvailable();
  }

  /**
   * settings page «Entfernen»: no longer offline, neither by hand nor automatically. With queued uploads the project
   * stays as «wartet auf Upload» (retained) and leaves once its outbox is empty (Schritt-1 FE-b minor 3, re-review H13).
   */
  async remove(projectId: string): Promise<void> {
    const userId = this.store.currentUserId();
    if (!userId) return;
    const busy = await this.hasOutbox(userId, projectId);
    await this.store.updateSettings(userId, (settings) => {
      const others = settings.retainedProjectIds.filter((id) => id !== projectId);
      return {
        ...settings,
        projectIds: settings.projectIds.filter((id) => id !== projectId),
        lastOpenedProjectId: settings.lastOpenedProjectId === projectId ? null : settings.lastOpenedProjectId,
        retainedProjectIds: busy ? [...others, projectId] : others,
      };
    });
    if (!busy) await this.store.deleteSnapshot(projectId, 'project', projectId, userId);
    await this.dropDocuments(userId, projectId);
    await this.reloadAvailable();
  }

  /**
   * «Für offline speichern» (spec §3): the file as one record. The project becomes «Offline verfügbar» (plan E1), so its
   * snapshot (document list, pins, rights) stays fresh and every refresh checks the saved files (plan E5). A full
   * device throws OfflineQuotaError after the quota hint. Only for the user who started the download (review W4).
   * Returns true when stored, false when another user signed in meanwhile (nothing written).
   * background (E14 revised, the sync run keeps an uploaded plan, Rev1 H3): no persist question (Firefox would ask and
   * hold the run), no quota toast (the upload itself succeeded), a missing project snapshot is loaded without waiting.
   */
  async saveDocument(projectId: string, data: OfflineDocumentData, userId: string, options: { background?: boolean } = {}): Promise<boolean> {
    const background = options.background ?? false;
    if (!this.writesFor(userId)) return false;
    const { persistRequested } = await this.store.getSettings(userId);
    if (!persistRequested && !background) await this.requestPersist();
    // the permission prompt may take long: never write the file for a user who is no longer signed in (NA-26)
    const stored = await this.guarded(userId, async () => {
      try {
        await this.store.putSnapshot(
          { projectId, kind: 'document', refId: data.documentId, name: data.name, etag: null, data, fetchedAt: this.now(), size: data.size },
          userId,
        );
      } catch (error) {
        if (error instanceof OfflineQuotaError && !background) this.quotaSubject.next();
        throw error;
      }
      // the removal waits for this section: the settings row of a removed user must not come back (Rev1 review I1)
      await this.store.updateSettings(userId, (settings) => ({
        ...settings,
        projectIds: settings.projectIds.includes(projectId) ? settings.projectIds : [...settings.projectIds, projectId],
        documentIds: settings.documentIds.includes(data.documentId) ? settings.documentIds : [...settings.documentIds, data.documentId],
        persistRequested: settings.persistRequested || !background,
      }));
      return true;
    });
    if (!stored) return false;
    if (!(await this.store.getSnapshot(projectId, 'project', projectId, userId))) {
      if (background) {
        void this.fetch(projectId, userId).catch((error: unknown) => console.warn('[OfflineAvailabilityService] project snapshot could not be loaded', error));
      } else {
        await this.fetch(projectId, userId);
      }
    }
    await this.reloadAvailable();
    return true;
  }

  async removeDocument(projectId: string, documentId: string): Promise<void> {
    const userId = this.store.currentUserId();
    if (!userId) return;
    await this.store.updateSettings(userId, (settings) => ({
      ...settings,
      documentIds: settings.documentIds.filter((id) => id !== documentId),
    }));
    await this.store.deleteSnapshot(projectId, 'document', documentId, userId);
  }

  async projectStatus(projectId: string): Promise<OfflineProjectStatus> {
    const settings = await this.store.getSettings();
    const snapshot = await this.store.getSnapshot(projectId);
    const documents = (await this.store.listSnapshots()).filter((record) => record.kind === 'document' && record.projectId === projectId);
    return {
      manual: settings.projectIds.includes(projectId),
      auto: settings.lastOpenedProjectId === projectId,
      fetchedAt: snapshot?.fetchedAt ?? null,
      size: snapshot?.size ?? 0,
      documentCount: documents.length,
      documentSize: documents.reduce((sum, record) => sum + record.size, 0),
    };
  }

  /**
   * refresh of a project that is offline (manual, last opened or retained); others are skipped. fresh: the data must
   * be newer than this call (after a sync), so a running request is not joined but followed (Schritt-1 FE-a M1).
   */
  async refresh(projectId: string, options: { fresh?: boolean } = {}): Promise<RefreshResult> {
    const userId = this.store.currentUserId();
    if (!userId || !(await this.trackedIds(userId)).has(projectId)) return 'skipped';
    return this.fetch(projectId, userId, true, options.fresh ?? false);
  }

  async refreshAll(): Promise<void> {
    const userId = this.store.currentUserId();
    if (!userId) return;
    for (const projectId of await this.trackedIds(userId)) await this.refresh(projectId);
  }

  async getProjectSnapshot<T>(projectId: string): Promise<T | null> {
    return ((await this.store.getSnapshot<T>(projectId))?.data ?? null) as T | null;
  }

  async summary(): Promise<OfflineSummary> {
    const settings = await this.store.getSettings();
    const items = (await this.store.listSnapshots()).map((record: SnapshotRecord): OfflineItemSummary => {
      const manual =
        record.kind === 'project' ? settings.projectIds.includes(record.projectId) : settings.documentIds.includes(record.refId);
      return {
        kind: record.kind,
        projectId: record.projectId,
        refId: record.refId,
        name: record.name,
        size: record.size,
        fetchedAt: record.fetchedAt,
        manual,
        // re-review H13: kept only for open uploads, shown as «wartet auf Upload» instead of «automatisch»
        retained: record.kind === 'project' && !manual && settings.retainedProjectIds.includes(record.projectId),
      };
    });
    const byName = (a: OfflineItemSummary, b: OfflineItemSummary) => a.name.localeCompare(b.name, 'de');
    return {
      projects: items.filter((item) => item.kind === 'project').sort(byName),
      documents: items.filter((item) => item.kind === 'document').sort(byName),
      totalSize: items.reduce((sum, item) => sum + item.size, 0),
      estimate: await this.store.estimate(),
    };
  }

  /** iOS deletes website data after 7 days without a visit unless the app is installed (spec §3) */
  isStandalone(): boolean {
    const legacy = (navigator as Navigator & { standalone?: boolean }).standalone === true;
    return legacy || !!window.matchMedia?.('(display-mode: standalone)').matches;
  }

  /**
   * Gate E-2 a: the user removes his offline data at logout. No new write section starts for him (guarded), and the
   * promise resolves once the sections already running (IndexedDB writes only, never an HTTP wait) have finished, so
   * the removal that follows deletes after the last write (Rev1 review H5, I1). The next sign-in lifts it (thawUser).
   */
  async freezeUser(userId: string): Promise<void> {
    this.frozenUsers.add(userId);
    if (this.store.currentUserId() === userId) this.clearAvailable();
    await Promise.allSettled([...(this.writing.get(userId) ?? [])]);
  }

  thawUser(userId: string): void {
    this.frozenUsers.delete(userId);
  }

  /** runs an IndexedDB write section of this user; not started for a frozen or signed-out user, awaited by freezeUser */
  private async guarded<T>(userId: string, section: () => Promise<T>): Promise<T | undefined> {
    if (!this.writesFor(userId)) return undefined;
    const running = section();
    const sections = this.writing.get(userId) ?? new Set<Promise<unknown>>();
    sections.add(running);
    this.writing.set(userId, sections);
    try {
      return await running;
    } finally {
      sections.delete(running);
      if (sections.size === 0) this.writing.delete(userId);
    }
  }

  /** writes for this user are still wanted: he is signed in and did not remove his data (Rev1 H5) */
  private writesFor(userId: string): boolean {
    return this.store.currentUserId() === userId && !this.frozenUsers.has(userId);
  }

  /** session begin/end (FE-G6): no checkmarks of the previous user until reloadAvailable() ran for the new one */
  clearAvailable(): void {
    this.availableSubject.next(new Set());
    this.chosenSubject.next(new Set());
  }

  /** after login: the checkmarks of the previous user must not stay (review H10) */
  async reloadAvailable(): Promise<void> {
    const userId = this.store.currentUserId();
    if (!userId) {
      this.clearAvailable();
      return;
    }
    const tracked = await this.trackedIds(userId);
    const { projectIds: chosen } = await this.store.getSettings(userId);
    const stored = (await this.store.listSnapshots(userId)).filter((record) => record.kind === 'project').map((record) => record.projectId);
    // the session changed while reading: these checkmarks belong to the previous user
    if (!this.writesFor(userId)) return;
    this.availableSubject.next(new Set(stored.filter((projectId) => tracked.has(projectId))));
    this.chosenSubject.next(new Set(stored.filter((projectId) => chosen.includes(projectId))));
    // a sign-in after the app start (login, signup, reset link): the start found no user to warm the viewer for (NA-20)
    void this.warmPdfViewer();
  }

  /**
   * One request per project at a time (review W6); the answer only lands at the user who asked (review W4). A call
   * joins the running request only when it asks the same (requireTracked) and needs no newer data (fresh); otherwise
   * it waits for one follow-up request after the running one (Schritt-1 FE-a M1, Task 12 minor).
   */
  private fetch(projectId: string, userId: string, requireTracked = false, fresh = false): Promise<RefreshResult> {
    const key = `${userId}:${projectId}`;
    const running = this.inFlight.get(key);
    if (running && !fresh && running.requireTracked === requireTracked) return running.promise;
    if (running) {
      const followKey = `${key}:${requireTracked}`;
      const queued = this.followUps.get(followKey);
      if (queued) return queued;
      const next = running.promise.catch(() => undefined).then(() => {
        this.followUps.delete(followKey);
        return this.fetch(projectId, userId, requireTracked);
      });
      this.followUps.set(followKey, next);
      return next;
    }
    const promise = this.load(projectId, userId, requireTracked).finally(() => this.inFlight.delete(key));
    this.inFlight.set(key, { promise, requireTracked });
    return promise;
  }

  private async load(projectId: string, userId: string, requireTracked: boolean): Promise<RefreshResult> {
    if (!navigator.onLine) return 'skipped';
    // after an offline cold start the tab holds no token yet: refresh first instead of a 401 round trip (NA-51)
    if (!(await this.auth.ensureAccessToken()) || !this.writesFor(userId)) return 'skipped';
    const existing = await this.store.getSnapshot(projectId, 'project', projectId, userId);
    let headers = new HttpHeaders({ 'ngsw-bypass': 'true' });
    if (existing?.etag) headers = headers.set('If-None-Match', existing.etag);
    // creates synced before this moment are in the answer (NA-03): a later one may not be
    const requestedAt = this.now();
    try {
      const response = await firstValueFrom(this.http.get<SnapshotResponse>(snapshotUrl(projectId), { headers }));
      const written = await this.guarded<RefreshResult>(userId, async () => {
        // a refresh answer for a project dropped meanwhile must not leave an orphan snapshot
        if (requireTracked && !(await this.trackedIds(userId)).has(projectId)) return 'skipped';
        if (!this.writesFor(userId)) return 'skipped';
        const data = response.data;
        await this.store.putSnapshot(
          {
            projectId,
            kind: 'project',
            refId: projectId,
            name: data.project.projectName,
            etag: data.etag,
            data,
            fetchedAt: this.now(),
            size: new Blob([JSON.stringify(data)]).size,
          },
          userId,
        );
        await this.store.pruneSynced(projectId, requestedAt, userId);
        await this.reconcileDocuments(projectId, data, userId);
        await this.reloadAvailable();
        return 'updated';
      });
      return written ?? 'skipped';
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 304 && existing) {
        const kept = await this.guarded<RefreshResult>(userId, async () => {
          if (requireTracked && !(await this.trackedIds(userId)).has(projectId)) return 'skipped';
          if (!this.writesFor(userId)) return 'skipped';
          await this.store.putSnapshot(
            {
              projectId: existing.projectId,
              kind: existing.kind,
              refId: existing.refId,
              name: existing.name,
              etag: existing.etag,
              data: existing.data,
              size: existing.size,
              fetchedAt: this.now(),
            },
            userId,
          );
          await this.store.pruneSynced(projectId, requestedAt, userId);
          return 'unchanged';
        });
        return kept ?? 'skipped';
      }
      // 403/404 from the Bavox API: no member any more or the project is gone; its data and files leave the device
      // (plan E5). A 403/404 without the API error format (proxy, WAF, captive portal, deploy page) keeps everything.
      if (error instanceof HttpErrorResponse && (error.status === 403 || error.status === 404) && isApiError(error.error)) {
        await this.dropLostProject(projectId, userId);
        return 'failed';
      }
      if (error instanceof OfflineQuotaError) this.quotaSubject.next();
      return 'failed';
    }
  }

  private async trackedIds(userId: string): Promise<Set<string>> {
    const settings = await this.store.getSettings(userId);
    const ids = new Set([...settings.projectIds, ...settings.retainedProjectIds]);
    if (settings.lastOpenedProjectId) ids.add(settings.lastOpenedProjectId);
    return ids;
  }

  /** re-review H13: a retained project leaves the device once its outbox is empty (unless manual or last opened) */
  private async pruneRetained(userId: string): Promise<void> {
    const outbox = await this.store.listOutbox(userId);
    let toDelete: string[] = [];
    await this.store.updateSettings(userId, (settings) => {
      toDelete = [];
      const emptied = settings.retainedProjectIds.filter((id) => !outbox.some((entry) => entry.entityRef.projectId === id));
      if (emptied.length === 0) return undefined;
      toDelete = emptied.filter((id) => !settings.projectIds.includes(id) && settings.lastOpenedProjectId !== id);
      return { ...settings, retainedProjectIds: settings.retainedProjectIds.filter((id) => !emptied.includes(id)) };
    });
    for (const id of toDelete) await this.store.deleteSnapshot(id, 'project', id, userId);
  }

  /** bundled synced$ (review W6): first drop emptied retained projects (re-review H13), then refresh the rest */
  private async afterSync(projectIds: string[]): Promise<void> {
    const userId = this.store.currentUserId();
    if (userId) {
      await this.pruneRetained(userId);
      await this.reloadAvailable();
    }
    for (const projectId of projectIds) void this.refresh(projectId, { fresh: true });
  }

  /**
   * App start: with a saved PDF the pdf.js files are fetched through the service worker again. After an app update they
   * have new file names (version in the name) and are not cached yet (Rev1 W5).
   */
  private async warmPdfViewer(): Promise<void> {
    try {
      const userId = this.store.currentUserId();
      if (!userId || !navigator.onLine) return;
      const records = await this.store.listSnapshots(userId);
      const hasPdf = records.some((record) => record.kind === 'document' && (record.data as OfflineDocumentData | null)?.mime === 'application/pdf');
      if (hasPdf) await this.warmup.warm(pdfViewerNeedsLegacyBuild());
    } catch (error) {
      // best effort: the next start, sign-in or online event tries again
      console.warn('[OfflineAvailability] pdf viewer warm-up failed', error instanceof Error ? error.name : 'unknown');
    }
  }

  private async documentsOf(userId: string, projectId: string): Promise<SnapshotRecord[]> {
    return (await this.store.listSnapshots(userId)).filter((record) => record.kind === 'document' && record.projectId === projectId);
  }

  /** the user switched the project off or removed it: its saved files go without a message */
  private async dropDocuments(userId: string, projectId: string): Promise<void> {
    const documents = await this.documentsOf(userId, projectId);
    if (documents.length === 0) return;
    const ids = new Set(documents.map((record) => record.refId));
    await this.store.updateSettings(userId, (settings) => ({ ...settings, documentIds: settings.documentIds.filter((id) => !ids.has(id)) }));
    for (const record of documents) await this.store.deleteSnapshot(projectId, 'document', record.refId, userId);
  }

  /**
   * After a new project snapshot (spec §3 refresh): a saved file whose document is gone (deleted) or superseded
   * (replaced by a newer version) leaves the device (plan E5). Without a document list nothing is removed.
   */
  private async reconcileDocuments(projectId: string, data: Record<string, unknown>, userId: string): Promise<void> {
    if (!Array.isArray(data['documents'])) return;
    const saved = await this.documentsOf(userId, projectId);
    if (saved.length === 0) return;
    // an entry without id (null, broken answer) is skipped, not a TypeError that stops the check (NA-26)
    const listed = (data['documents'] as unknown[]).filter(
      (document): document is { id: string; superseded?: boolean } => typeof (document as { id?: unknown } | null)?.id === 'string',
    );
    const current = new Map(listed.map((document) => [document.id, document]));
    const removed: DocumentRemovedEvent[] = [];
    for (const record of saved) {
      const document = current.get(record.refId);
      const reason = !document ? 'deleted' : document.superseded ? 'replaced' : null;
      if (!reason) continue;
      await this.store.deleteSnapshot(projectId, 'document', record.refId, userId);
      removed.push({ projectId, documentId: record.refId, name: record.name, reason });
    }
    if (removed.length === 0) return;
    const ids = new Set(removed.map((event) => event.documentId));
    await this.store.updateSettings(userId, (settings) => ({ ...settings, documentIds: settings.documentIds.filter((id) => !ids.has(id)) }));
    // signed out or another user meanwhile: no toast with the previous user's plan names (Task 2 M4)
    if (!this.writesFor(userId)) return;
    this.documentsRemovedSubject.next(removed);
  }

  /**
   * The snapshot answers 403/404: no member any more or the project was deleted (spec §3 «Zugriff fällt weg»). Project
   * data and saved files leave the device; open outbox entries stay (they fail visibly with «Kopieren», spec §6).
   */
  private async dropLostProject(projectId: string, userId: string): Promise<void> {
    await this.guarded(userId, () => this.dropLost(projectId, userId));
  }

  private async dropLost(projectId: string, userId: string): Promise<void> {
    const documents = await this.documentsOf(userId, projectId);
    const ids = new Set(documents.map((record) => record.refId));
    await this.store.updateSettings(userId, (settings) => ({
      ...settings,
      projectIds: settings.projectIds.filter((id) => id !== projectId),
      documentIds: settings.documentIds.filter((id) => !ids.has(id)),
      lastOpenedProjectId: settings.lastOpenedProjectId === projectId ? null : settings.lastOpenedProjectId,
      retainedProjectIds: settings.retainedProjectIds.filter((id) => id !== projectId),
    }));
    for (const record of documents) await this.store.deleteSnapshot(projectId, 'document', record.refId, userId);
    await this.store.deleteSnapshot(projectId, 'project', projectId, userId);
    if (documents.length > 0) {
      this.documentsRemovedSubject.next(documents.map((record) => ({ projectId, documentId: record.refId, name: record.name, reason: 'access' })));
    }
    await this.reloadAvailable();
  }

  private async hasOutbox(userId: string, projectId: string): Promise<boolean> {
    return (await this.store.listOutbox(userId)).some((entry) => entry.entityRef.projectId === projectId);
  }

  private async requestPersist(): Promise<void> {
    try {
      await navigator.storage?.persist?.();
    } catch {
      // not supported or refused: the hint on the page «Offline-Speicher» stays
    }
  }
}
